import React, {
  forwardRef,
  useEffect,
  useContext,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import _ from 'lodash'
import clsx from 'clsx'
import ItemList from '../ItemList'
import useGanttItemLoader from '../useGanttItemLoader'
import PillRows from '../PillRows'
import './GanttGroup.scss'
import LoadingIndicator from '../../../ReservationConfiguration/LoadingIcon'
import SelectionRangeIndicator from '../SelectionRangeIndicator'
import {ReservationContext} from '../../../../context/Reservation'
import moment from 'moment'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import {IconButton} from '@material-ui/core'
import WalkingPanda from '../../../../common/LoadingIndicators/WalkingPanda'
import useInitLoader from '../../../ReservationNewCalendar/useInitLoader'

const RootClass = 'ap-reservation-gantt'

const GanttGroup = forwardRef(
  (
    {
      columnHeaders,
      viewBreakPoint,
      title = null,
      groupBy = null,
      filterValue,
      index,
      filterData,
      applied_filters = null,
    },
    ref
  ) => {
    const [windowWResize, setWindowWResize] = useState(document.documentElement.clientWidth)
    const {resources, setLimit, loadItems, setAdditionalFilters} = useGanttItemLoader()
    const {reservation, update} = useContext(ReservationContext)
    const refTimelineEvents = useRef(null)
    const [expand, setExpand] = useState(true)

    const [showLoader, doneInit] = useInitLoader(resources.isLoading)
    const handleLoadMore = e => {
      loadItems({
        offset: resources.totals.offset + resources.totals.limit,
        limit: resources.totals.limit,
      })
    }

    useImperativeHandle(
      ref,
      () => ({
        loadMore() {
          // get limit
          if (
            !resources.isLoading &&
            resources.totals.offset + resources.totals.limit < resources.totals.objects
          ) {
            loadItems({
              offset: resources.totals.offset + resources.totals.limit,
              limit: resources.totals.limit,
            })
          }
        },
      }),
      [loadItems, resources]
    )

    useEffect(() => {
      let newSearch = ''
      if (filterData) {
        newSearch = filterData
      }
      if (groupBy?.key) {
        setLimit(5)
        setAdditionalFilters({[groupBy.key]: [filterValue], newSearch, applied_filters})
      }
      // eslint-disable-next-line
    }, [filterValue, groupBy, setLimit, setAdditionalFilters])

    useEffect(() => {
      if (columnHeaders.primary.length > 0) {
        const leftOffset = columnHeaders.primary[0].width - 26
        if (refTimelineEvents.current) {
          refTimelineEvents.current.scrollLeft = leftOffset
        }
      }
    }, [columnHeaders, resources, expand, windowWResize])

    useEffect(() => {
      const f = () => {
        setWindowWResize(document.documentElement.clientWidth)
      }
      setWindowWResize(document.documentElement.clientWidth)
      window.addEventListener('resize', f)
      return () => {
        window.removeEventListener('resize', f)
      }
    }, [])

    const allLoaded = resources.totals.offset + resources.totals.limit >= resources.totals.objects
    return (
      <>
        {index === -1 && (
          <WalkingPanda hidden={!showLoader || doneInit} style={{zIndex: 1299, paddingTop: 120}} />
        )}
        <div
          className={clsx('ap-typography ap-typography--sm m-reservation-gantt c-gantt-group', {
            'c-gantt-group--has-title': title !== null,
          })}
        >
          {title && (
            <div
              className="c-gantt-group__title"
              title={`${title} (${resources.totals.objects || 0})`}
            >
              <span className="ap-ellipsis">
                {title} ({resources.totals.objects || 0})
              </span>
              <IconButton
                size="small"
                className="ap-icon-button ap-icon-button--square"
                style={{color: '#596175'}}
                onClick={() => setExpand(p => !p)}
              >
                {expand ? <ExpandMoreIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </div>
          )}
          {expand && (
            <>
              <div className="c-gantt-group__items">
                <ItemList items={resources.data} />
              </div>
              <div className="c-gantt-group__events" style={{overflow: 'hidden'}}>
                <div
                  ref={refTimelineEvents}
                  className={`c-gantt__timeline-scroll ap-scrollbar`}
                  data-x="date-selection"
                >
                  {reservation.isSelection && (
                    <SelectionRangeIndicator
                      primaryHeaders={columnHeaders.primary}
                      startSec={moment(reservation.date_from).valueOf()}
                      endSec={moment(reservation.date_to).valueOf()}
                      items={resources.data}
                    />
                  )}

                  {/* Background */}
                  <div className={`${RootClass}__date-bg overlay-z-index`}>
                    {_.map(columnHeaders.primary, (e, i) => (
                      <div
                        key={i}
                        id={`col-heads-primary-${i}`}
                        style={{flex: `0 0 ${e.width}px`}}
                        className={`${RootClass}__column${
                          e.label_sub === 'Sun' || e.label_sub === 'Sat'
                            ? '--sat-sun-background'
                            : ''
                        }`}
                      ></div>
                    ))}
                  </div>

                  <PillRows
                    items={resources.data}
                    breakpoint={viewBreakPoint}
                    headers={columnHeaders}
                  />
                </div>
              </div>
              <div className="c-gantt-group__load">
                {!resources.isLoading && allLoaded && (
                  <div
                    className="ap-typography ap-typography--gray ap-typography--sm ap-typography--nowrap"
                    style={{flex: 1, padding: '0 32px'}}
                  >
                    No more items.
                  </div>
                )}
                {!resources.isLoading && !allLoaded && (
                  <div
                    className="ap-typography ap-typography--sm ap-typography--nowrap c-gantt-group__btn-load-more"
                    onClick={handleLoadMore}
                  >
                    Load More
                  </div>
                )}
                {resources.isLoading && <LoadingIndicator style={{flex: 1, padding: '0 32px'}} />}
              </div>
            </>
          )}
        </div>
      </>
    )
  }
)

export default GanttGroup
